import { type CallsIntent } from '@/cam-admin/control-panel/CallsTab';
import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import { type HandoverSpec } from '@/designs-dev/handover/types';

// One live control panel mini per step / state, opened on the Calls tab and
// jumped straight to a detail or the log-a-call form via the calls prop.
const screen = (calls?: CallsIntent) => (
  <ControlPanel embedded initialTab='calls' callsInitial={calls} />
);

// The CAM-side handover for Call Notes (part of Key Documents V0): CAMs log
// calls and write summaries on the control panel; the care circle reads them
// (read-only) in the member app. Calendly prefills the call metadata.
export const callsSpec: HandoverSpec = {
  id: 'calls',
  title: 'Call notes',
  screen: 'desktop',
  description:
    'CAMs log calls and write a summary against the care recipient, linking the documents and actions that came out of each one. The care circle reads the notes (read-only) in the member app, so the family always knows what was discussed and what happens next.',
  links: [
    {
      label: 'Project: Key Documents V0 (Linear)',
      url: 'https://linear.app/karehero/project/key-documents-v0-initial-setup-29e97808f0d9',
    },
    { label: 'Live prototype (CAM control panel)', url: '/care-search' },
    { label: 'Member app (Profile tab)', url: '/karehero-app/profile' },
    { label: 'Key documents handover', url: '/handover/documents' },
  ],
  flows: [
    {
      title: 'Log a call',
      steps: [
        {
          id: 'list',
          title: 'Calls list',
          tap: 'Control panel › Calls tab',
          render: () => screen(),
        },
        {
          id: 'log',
          title: 'Log a call',
          tap: 'Tap ‘Log a call’',
          render: () => screen({ kind: 'log' }),
        },
        {
          id: 'saved',
          title: 'Saved',
          tap: 'Save — the call appears in the list, the circle is notified',
          render: () => screen(),
        },
      ],
    },
    {
      title: 'Read a call and its links',
      steps: [
        {
          id: 'open',
          title: 'Call detail',
          tap: 'Open a call from the list',
          render: () => screen({ kind: 'open', id: 'c2' }),
        },
      ],
    },
  ],
  states: [
    {
      id: 'list',
      title: 'Calls list',
      kind: 'happy',
      repro: 'Calls tab. Upcoming and past calls, grouped',
      render: () => screen(),
    },
    {
      id: 'detail-notes',
      title: 'Call detail with summary',
      kind: 'happy',
      repro:
        'Open ‘Care assessment’ (c2). Summary, linked documents, linked actions',
      render: () => screen({ kind: 'open', id: 'c2' }),
    },
    {
      id: 'log-form',
      title: 'Log a call',
      kind: 'happy',
      repro: 'Tap ‘Log a call’. Calendly link prefills the metadata fields',
      render: () => screen({ kind: 'log' }),
    },
    {
      id: 'upcoming',
      title: 'Upcoming call, no notes yet',
      kind: 'edge',
      repro: 'Open ‘Funding call’ (c3, upcoming) — no summary until it happens',
      render: () => screen({ kind: 'open', id: 'c3' }),
    },
    {
      id: 'edit',
      title: 'Edit a summary',
      kind: 'happy',
      repro:
        'Open a past call, tap Edit — the note textarea with Save / Discard',
      render: () => screen({ kind: 'open', id: 'c1' }),
    },
  ],
  requirements: [
    {
      text: 'Log a call: date, time, duration, type, who was on it, summary notes',
      status: 'built',
      where: 'Calls tab › Log a call',
    },
    {
      text: 'Paste a Calendly link to prefill the call metadata (all editable)',
      status: 'built',
      where: 'Log-a-call form',
      note: 'Prefill is mocked from the pasted link',
    },
    {
      text: 'Calls list: upcoming and past, grouped, searchable',
      status: 'built',
      where: 'Calls tab',
      note: 'Search field is present, not yet wired to filter',
    },
    {
      text: 'Call detail: summary, metadata, edit the note',
      status: 'built',
      where: 'Open a call',
    },
    {
      text: 'Link documents to a call',
      status: 'partial',
      where: 'Call detail › Linked documents',
      note: 'Upload-to-link is mocked in the detail rail; not wired to storage',
    },
    {
      text: 'Link actions to a call, split into before / after the call',
      status: 'built',
      where: 'Call detail › Linked actions',
    },
    {
      text: 'Carer mobile UI: call-notes list (CAM avatar, name, date, preview, ‘New’ badge) and detail',
      status: 'built',
      where: 'Member app › Care hub › Calls',
      note: 'Built in the member prototype (CallSummary). Read-only',
    },
    {
      text: 'Read-only for the care circle — carers can’t edit, delete or respond',
      status: 'built',
      where: 'Member app › Care hub › Calls',
    },
    {
      text: 'Multi-CAM display (which CAM wrote which note)',
      status: 'partial',
      note: 'Notes record who was on the call; per-CAM authorship not surfaced',
    },
    {
      text: 'In-app notification to the care circle when a CAM saves new call notes',
      status: 'partial',
      note: 'Save toast says the circle is notified. No notification fires',
    },
    {
      text: 'Empty and error states for the member call-notes list',
      status: 'built',
      where: 'Member app › Care hub › Calls',
    },
  ],
  components: {
    mui: [
      'Avatar',
      'Button',
      'Card',
      'Checkbox',
      'InputAdornment',
      'TextField',
    ],
    existing: [
      'Icon (llama)',
      'PanelCard',
      'TwoColumn',
      'BackLink',
      'GroupLabel',
    ],
    new: ['CallRow', 'LinkedActionRow', 'NoteEditor', 'CallSummary'],
  },
  componentsNote:
    'CallRow / LinkedActionRow / NoteEditor are the CAM side (Calls tab); CallSummary is the carer mobile read-only view (member app). PanelCard, TwoColumn, BackLink and GroupLabel are the shared control-panel layout bits.',
};
